export interface Store {
  storeId: number;
  chainId: number;
  storeName: string;
  storeCode: string | null;
  legibleName: string;
  timeZone: string;
  openTime: string;
  closeTime: string;
  chainName: string;
  chainLegibleName: string;
  isActive: boolean;
}

export type Rect = [number, number, number, number]; // x, y, width, height

export interface BBox {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  productId: number;
  itemNumber: string;
  upc: string;
  name: string | null;
  imageUri: string | null;
  productImageUri?: string | null;
  hasFallbackImage?: boolean;
  modCoverageId: number;
  templateId?: number;
  shelf?: number;
  column?: number;
  shelfColumnLabel?: string; // Format: "mod-shelf-column" e.g., "1-2-3"
  [key: string]: any; // Allow additional properties
}

export interface TemplateBBox extends BBox {
  tagName: string;
  bboxId: number;
}

export interface PogBBox extends BBox {
  embeddingCropCount: number | null;
  ilItemId: number;
  ilSectionId: number;
  pogImageUri: string;
  shelf?: number;
  column?: number;
  isUnstructured?: boolean;
  isActive: boolean;
}

export interface BoxAction {
  bboxId: number;
  tagName: string;
  productId?: number;
  upc: string;
  oosDetection?: OosDetectionState;
  occupancy?: number;
  produceIssue?: 'faceOver' | 'presentability';
  commodityFound?: string[];
  expectedCommodity?: string;
}

export enum OosDetectionState {
  IN = 'in',
  LOW = 'low',
  OUT = 'out',
  NA = 'n/a',
  ZIN = 'zin',
}

export enum RogItemStatus {
  CORRECT = 'correct',
  MOVE = 'move',
  OUT = 'out',
  REMOVE = 'remove',
  NA = 'na',
}

export enum ComplianceStatus {
  COMPLIANT = 'compliant',
  NON_COMPLIANT = 'non_compliant',
  OUT = 'out',
  FACEOVER = 'faceover',
}

export enum ImageQuality {
  UNKNOWN = 'unknown',
  OK = 'ok',
  SHIFTED = 'shifted',
  INCONSISTENT = 'inconsistent',
  OBSTRUCTED = 'obstructed',
}

export enum CoffeeMachineState {
  UNKNOWN = 'unknown',
  ON = 'on',
  OFF = 'off',
  ERROR = 'error',
  OBSTRUCTED = 'obstructed',
}

export enum MessinessState {
  UNKNOWN = 'unknown',
  MESSY = 'messy',
  CLEAN = 'clean',
  OBSTRUCTED = 'obstructed',
}

export enum CupsState {
  UNKNOWN = 'unknown',
  MISSING = 'missing',
  PRESENT = 'present',
  OBSTRUCTED = 'obstructed',
}

export enum LidsState {
  UNKNOWN = 'unknown',
  MISSING = 'missing',
  PRESENT = 'present',
  OBSTRUCTED = 'obstructed',
}

export enum ProduceVisionIssues {
  SPOILED = 'spoiled',
  UNFRESH = 'unfresh',
  OUT = 'out',
  LOW = 'low',
  OVERFLOW = 'overflow',
  MIXED = 'mixed',
  WRONG_BOX = 'wrong_box',
  OCCLUSION = 'occlusion',
  IMAGE_DEFECT = 'image_defect',
  POG_NONCOMPLIANT = 'pog_noncompliant',
  MOD_OUT_OF_SCOPE = 'mod_out_of_scope',
  BOX_UNPAIRED = 'box_unpaired',
}

export enum OcrConfidence {
  SKIPPED = '0',
  LOW = '1',
  MEDIUM = '2',
  HIGH = '3',
}

export type OosDetectionParams = {
  detections: {
    product_id?: number;
    upc?: string;
    state: OosDetectionState;
  }[];
  image_id?: number;
};

export type OosDetectionResponse = {
  label_items: {
    product_id?: number;
    upc?: string;
    state: OosDetectionState;
    oos_id?: number;
    task_id?: number;
    task_type?: string;
    task_state?: string;
  }[];
  device_id: number;
  device_name: string;
  camera_id: number;
  status_id: number;
  image_id: number;
  record_id: number;
  model_results: Record<string, any>;
};

export type FlexSpaceDetectionParams = {
  image_id?: number;
  template_id?: number;
  occupancies?: number[];
};

export type CoffeeDetectionParams = {
  image_id: number;
  image_quality: ImageQuality;
  coffee_machine_state: CoffeeMachineState;
  messiness_state: MessinessState;
  cups_state: CupsState;
  lids_state: LidsState;
};

export type ProduceVisionParams = {
  image_id: number;
  boxes_info: {
    bbox_id: number;
    pog_name?: string;
    expected_commodity?: string;
    commodity_found: string[];
    issues: ProduceVisionIssues[];
    occupancy_area: number | null;
  }[];
};

export type DeviceWakeUpParams = {
  image_id: number | null;
  boot_id: number | null;
  ota_version: string | null;
  config: Record<string, any> | null;
  schedule_id: string | null;
  status_payload: Record<string, any> | null;
  image_payload: Record<string, any> | null;
  skip_image: boolean | null;
  alarm_wake_up: boolean | null;
};

export interface CameraImageResponse {
  chainId: number;
  storeId: number;
  deviceName?: string | null;
  storeName: string;
  cameraId: number;
  image: CameraImage;
  pogs: Record<string, CameraPog>;
  templates: Record<string, CameraTemplate>;
}

export interface CameraImageTimelineResponse {
  images: CameraImage[];
  templates: Record<string, CameraTemplate>;
  pogs: Record<string, CameraPog>;
}

export interface CameraImage {
  imageId: number;
  imageUri: string;
  detectedAt: string; // ISO date string
  templateId: number;
  ilSectionIds: number[];
}

export interface CameraTemplate {
  templateId: number;
  bboxes: TemplateBBox[];
}

export interface CameraPog {
  ilSectionId: number;
  modCoverageId: number;
  pogImageUri: string;
  tagName: string;
  bboxes: PogBBox[];
}

export interface CameraDetails {
  deviceName: string;
  cameraId: number;
  imageUri: string;
  imageCreatedAt: string; // ISO date string
  templateCreatedAt: string; // ISO date string
  pogCreatedAt: string; // ISO date string
  pogImageUri: string;
  mods: Mod[];
}

export interface Mod {
  modCoverageId: number;
  department: string;
  tagName: string;
  processingType: ProcessingType;
  isComplianceEnabled: boolean;
  complianceEligibilitySource: string;
  complianceEligibilityUpdatedAt: string;
  complianceEligibilityUpdatedByUserId: number;
  complianceEligibilityUpdatedByEmail: string;
}

export type ProcessingType = 'structured' | 'flex-space' | 'structured-produce' | 'coffee-machine';

export type ControlModEligibilityBatchRequest = {
  modCoverageIds: number[];
};

export type ControlModEligibilityBatchResponse = {
  updatedCount: number;
};

export type ProductRecognitionLayerType =
  | 'productBoxes'
  | 'productPolygons'
  | 'cartonBoxes'
  | 'cartonPolygons'
  | 'priceTagBoxes'
  | 'modPolygons'
  | 'shelfBoxes'
  | 'templateBoxes'
  | 'templateBoxesUp'
  | 'visualObstructions'
  | 'merchandise'
  | 'hookArea'
  | 'fridgeDoors'
  | 'topStock'
  | 'shelfEdges';

export enum ShapeType {
  PRODUCT = 'product',
  CARTON = 'carton',
  TEMPLATE_BOX = 'template_box',
  TEMPLATE_BOX_UP = 'template_box_up',
  HOOK_AREA = 'hook_area',
  FRIDGE_DOOR = 'fridge_door',
  MERCHANDISE = 'merchandise',
  MISPLACED_ITEM = 'misplaced_item',
  CART_OR_PRODUCT = 'cart_or_product',
  BLACK_MASK = 'black_mask',
  PRICE_TAG = 'price_tag',
  MOD = 'mod',
  SHELF_AREA = 'shelf_area',
  SHELF_EDGE = 'shelf_edge',
  TOP_STOCK = 'top_stock',
  FIXTURES = 'fixtures',
  LENSE_BLOCKING = 'lense_blocking',
}

export interface OCRResponse {
  text?: string;
  confidence?: OcrConfidence;
}

export interface ProductRecognitionShape {
  type?: ShapeType;
  confidence?: number;
  ocr?: OCRResponse;
  column?: number;
  mod?: number;
  shelf?: number;
  vizSimCandidates?: VizSimCandidate[];
}

export type VizSimReferenceCrop = {
  embeddingCropId: string;
  similarity: number;
};

export type VizSimCandidate = {
  normalizedBarcode: string;
  similarity: number | null;
  isConfident: boolean;
  productName?: string;
  itemNumber?: string;
  productImageUri?: string;
  productId?: number;
  isExpectedPog?: boolean;
  referenceCrops?: VizSimReferenceCrop[];
  allNormalizedBarcodes?: string[];
  isActive: boolean;
};

export interface ProductRecognitionBBox extends ProductRecognitionShape {
  x1: number;
  x2: number;
  y1: number;
  y2: number;
  shape: [number, number, number, number]; // [x, y, width, height]
}

export interface PlanogramComplianceBox {
  imageId: number;
  rogItemId: number | null;
  rogItemUpc: string | null;
  x1: number | null;
  y1: number | null;
  x2: number | null;
  y2: number | null;
  templateCoordinates: [number, number, number, number] | null; // [x1, y1, x2, y2]
  shape: [number, number, number, number] | null; // [x, y, width, height] - bounding box of polygon
  rogItemStatus: RogItemStatus;
  rogPositionVector: {
    mod: number;
    shelf: number;
    position: number;
    tagName: string;
    modCoverageId: number;
  };
  pogItemId: number;
  pogItemUpc: string;
  osaStatus: OosDetectionState;
  complianceStatus: ComplianceStatus;
  pogPositionVector: {
    mod: number;
    shelf: number;
    position: number;
    tagName: string;
    modCoverageId: number;
  };
  productName: string;
  itemNumber: string;
  productImageUri: string;
}

export interface PolygonPoint {
  x: number;
  y: number;
}

export interface ProductRecognitionPolygon extends ProductRecognitionShape {
  points: PolygonPoint[];
  shape: [number, number, number, number]; // [x, y, width, height] - bounding box of polygon
}

export interface ProductRecognitionMask extends ProductRecognitionShape {
  pngData: {
    b64: string;
  };
  shape: [number, number, number, number]; // [x, y, width, height] - added at runtime by api.ts
}

export interface InferenceMetadata {
  ssm_plus_version?: string;
  visual_embedding_model_version?: string;
  oos_model_version?: string;
  steps_run?: string[];
  [key: string]: unknown;
}

export interface ProductRecognitionResponse {
  imageId?: number;
  inferenceId: string;
  inferenceMetadata?: InferenceMetadata;
  merchandise: ProductRecognitionMask[];
  productBoxes: ProductRecognitionBBox[];
  productPolygons: ProductRecognitionPolygon[];
  cartonBoxes: ProductRecognitionBBox[];
  cartonPolygons: ProductRecognitionPolygon[];
  priceTagBoxes: ProductRecognitionBBox[];
  modPolygons: ProductRecognitionPolygon[];
  shelfBoxes: ProductRecognitionBBox[];
  templateBoxes: ProductRecognitionBBox[];
  templateBoxesUp: ProductRecognitionBBox[];
  visualObstructions: ProductRecognitionMask[];
  hookArea: ProductRecognitionMask[];
  fridgeDoors: ProductRecognitionMask[];
  topStock: ProductRecognitionMask[];
  shelfEdges: ProductRecognitionMask[];
  corrections?: ProductRecognitionCorrection[];
  updatedAt?: string;
  isV2?: boolean;
}

export interface ProductRecognitionCorrection {
  shapeList: string;
  index: number;
  upc: string;
  itemNumber?: string;
}

export interface PlanogramComplianceResponse {
  imageId: number;
  boxes: PlanogramComplianceBox[];
  osaCompliance: Record<string, number>;
  pogCompliance: Record<string, number>;
}

export interface PlanogramComplianceConfigItem {
  key: string;
  type: 'number' | 'boolean' | 'enum' | 'multi_enum';
  defaultValue: number | boolean | string | string[];
  value: number | boolean | string | string[];
  label: string;
  options: string[] | null;
}

export interface PlanogramCompliancePayload extends ProductRecognitionResponse {
  config?: Record<string, unknown>;
}

// Embedding types
export type ProductEmbeddingCropSource = 'at' | 'control' | 'scan' | 'other';

export type ProductEmbeddingCropSortField =
  | 'created_at'
  | 'updated_at'
  | 'normalized_barcode'
  | 'created_by'
  | 'store_id'
  | '_id';

export type HttpSortingOrderEnum = 'asc' | 'desc';

export type ImageResolution = '5mp' | '8mp';

export type Orientation = 'front' | 'not-front';

export type ClassificationType = 'product' | 'carton' | 'template_box';

export interface Coords {
  bbox: [number, number, number, number];
}

export interface CropEmbeddingSearchResponse {
  productEmbeddingCropId?: string;
  storeId: number;
  chainId: number;
  barcode: string;
  normalizedBarcode?: string;
  productName?: string;
  isSynthetic: boolean;
  manualReview: boolean;
  source: ProductEmbeddingCropSource;
  imageId: number;
  imageResolution: ImageResolution;
  coords: Coords;
  vizUrl: string;
  packageVariant?: string;
  orientation: Orientation;
  classificationType: ClassificationType;
  transformationMatrix?: string;
  isActive: boolean;
  isPending: boolean;
  createdBy: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  productImage?: string;
  itemNumber?: number;
  designId?: string | null;
  designName?: string | null;
}

export interface UpdatedEmbeddingsResponse {
  embeddingCropId: string;
  embeddingsCount: number;
}

export interface EmbeddingCountByBarcode {
  barcode: string;
  embeddingsCount: number;
}

export enum ProductImageSource {
  ShelfCam = 'shelf-cam',
  AutoCheckout = 'auto-checkout',
  Online = 'online',
  UserUpload = 'user-upload',
  Embedding = 'embedding',
}

export interface ProductImage {
  productImageId?: number;
  imageUri: string;
  position: number;
  chainId: number;
  source: ProductImageSource;
  origin?: string;
}

export interface ProductImageCreateResponse {
  product_image_id: number;
  upc: string;
  image_uri: string;
  position: number;
}

export interface ProductImageReorderItem {
  product_image_id: number;
  position: number;
}

export interface CandidatesSearchParams {
  chainId: number;
  upc: string;
  storeId?: number;
  sortField?: ProductEmbeddingCropSortField;
  sortOrder?: HttpSortingOrderEnum;
  page?: number;
  limit?: number;
}

export interface EmbeddingSearchParams {
  chainId: number;
  storeId?: number;
  normalizedBarcodes?: string;
  isActive?: boolean;
  source?: ProductEmbeddingCropSource;
  extendedSearch?: boolean;
  includeItemNumber?: boolean;
  itemNumber?: string;
  barcode?: string;
  createdBy?: string;
  sortField?: ProductEmbeddingCropSortField;
  sortOrder?: HttpSortingOrderEnum;
  page?: number;
  limit?: number;
}

// Product Recognition Crop Scan Types
export type ProductRecognitionCropScanResponse = {
  product_crop_scan_id: number;
  parent_product_crop_scan_id?: number;
  order: number;
  classification_type: ClassificationType;
  orientation_class: Orientation;
  valuable_to_classify: boolean;
  bbox: Coords['bbox'];
  points: object[];
  barcode?: string;
  normalized_barcode?: string;
  product_id?: number;
  product_name?: string;
  product_image_uri?: string;
};

export type ProductRecognitionImageCropScansResponse = {
  image_url: string;
  crop_scans: ProductRecognitionCropScanResponse[];
};

export type ProductRecognitionCropScanUpdate = {
  product_crop_scan_id: number;
  barcode?: never; // For now there is no way to tell which variant we are updating
  normalized_barcode: string;
  product_id: number | null;
  orientation?: 'front' | 'not-front';
  design_id?: string;
};

export type ProductVariantResponse = {
  upc: string;
  name: string | null;
  casePack: number | null;
  isCaseUpc: boolean;
  isActive: boolean;
  image: string | null;
  frontImage: string | null;
  backImage: string | null;
  itemNumber?: string;
};

export type ProductBarcodeResponse = {
  barcode: string;
  normalized_barcode: string;
  normalized_upc?: string;
  product_id?: number;
  product_name?: string;
  product_image_uri?: string;
  item_number?: string;
  inventory_level?: number;
};

export type StoreProductSearchResult = {
  case_upc?: string[] | null;
  imageUrl?: string | null;
  isActive?: boolean | null;
  itemNumber?: string | null;
  msiNumber?: string | null;
  name?: string | null;
  productId?: number | null;
  upc: string;
};

export type StoreProductSearchResponse = {
  products: StoreProductSearchResult[];
};

export type EmbeddingCandidateResponse = {
  bboxCoords: number[];
  bboxId: number;
  column: number;
  templateId: number;
  templateCreatedAt: string;
  imageId: number;
  imageUri: string;
  imageCreatedAt: string;
  imageResolution: ImageResolution;
  ilSectionId: number;
  ilItemId: number;
  pogImageUri: string;
  cameraId: number;
  deviceName: string;
  storeId: number;
  storeName: string;
  legibleName: string;
  tagName: string;
  pogBoxCoords: number[];
  pogImageId: number;
  shelf: number;
};

export type EmbeddingCandidate = EmbeddingCandidateResponse & {
  cropImageUri: string;
  pogCropImageUri: string;
};

export type StatusResponse = {
  status: string;
  message?: string;
};

// Product Recognition API Types
export type V1GetProductRecognitionCropScansApiResponse =
  /** status 200 Successful Response */ ProductRecognitionImageCropScansResponse;
export type V1GetProductRecognitionCropScansApiArg = {
  /** Image ID to get crop scans for */
  imageId: number;
};

export type V1CreateProductRecognitionCropScansApiResponse =
  /** status 200 Successful Response */ ProductRecognitionImageCropScansResponse;
export type V1CreateProductRecognitionCropScansApiArg = {
  /** Image ID to create crop scans for */
  imageId: number;
};

export type V1UpdateProductRecognitionCropScansApiResponse =
  /** status 200 Successful Response */ StatusResponse;
export type V1UpdateProductRecognitionCropScansApiArg = {
  imageId: number;
  payload: ProductRecognitionCropScanUpdate[];
};

export type V1FinishProductRecognitionCropScanSessionApiResponse =
  /** status 200 Successful Response */ StatusResponse;
export type V1FinishProductRecognitionCropScanSessionApiArg = {
  imageId: number;
};

export type V1GetStoreProductByBarcodeApiResponse =
  /** status 200 Successful Response */ ProductBarcodeResponse;
export type V1GetStoreProductByBarcodeApiArg = {
  /** Store name to lookup product in */
  storeName: string;
  /** Barcode to lookup */
  barcode: string;
};

// Task types
export interface Task {
  actionTaskId: number;
  aisleId: string | null;
  aisleName: string | null;
  cameraId: number;
  chainId: number;
  chainName: string;
  customTaskId: number;
  deviceName: string;
  imageCaptureTime: string;
  imageId: number;
  imageUri: string;
  isAudited: boolean;
  isActive: boolean;
  isReviewed: boolean;
  isWorked: boolean;
  itemAisle: string;
  itemAisleSide: string;
  itemDepartment: string;
  itemMod: string;
  itemShelfTagName: string;
  modCoverageId: number;
  numberOfSubtasks: number;
  osaComplianceScore: number;
  pogComplianceScore: number;
  productRecognitionInferenceId: number;
  lockedByUserId: string | null;
  lockedByUserName: string | null;
  reviewAssignedAt: string | null;
  storeId: number;
  storeName: string;
  tagName: string;
  taskCreatedAt: string;
  taskDate: string;
  taskId: number;
}

export interface TaskExtended extends Task {
  atUrl: string;
}

export type ReviewStatus = 'reviewed' | 'non_reviewed';
export type TaskSortField =
  | 'pog_compliance_score'
  | 'osa_compliance_score'
  | 'tag_name'
  | 'task_created_at'
  | 'image_capture_time';
export type TaskSortOrder = 'asc' | 'desc';

export interface TasksSearchParams {
  chainId: number;
  imageId?: string;
  storeId?: number;
  tagNamePrefix?: string;
  reviewStatus?: ReviewStatus;
  taskDate?: string; // YYYY-MM-DD format
  isActive?: boolean;
  isAudited?: boolean;
  page?: number;
  limit?: number;
  sortField?: TaskSortField;
  sortOrder?: TaskSortOrder;
}

export interface TasksListStorage {
  chainId: number;
  tasks: Task[];
}

export interface TaskPogResponse {
  pogs: Record<string, CameraPog>;
}

export enum EmbeddingSelectionMode {
  ACTIVATING = 'activating',
  DEACTIVATING = 'deactivating',
}

export interface UpcCorrection {
  shape_list: string;
  index: number;
  upc: string;
  item_number?: string;
}

export enum UpcCorrectionCanceledReason {
  OBSTRUCTED = 'obstruction',
  BLURRY = 'blurry',
  MISSING_PRODUCT_BOX = 'missing_product_box',
}

export interface UpcCorrectionPayload {
  corrections: UpcCorrection[];
  confidence: number;
  canceled?: UpcCorrectionCanceledReason;
}

export type TaskLockStatusResponse =
  | {
      lockedByUserId: string;
      lockedByUserName: string;
      reviewAssignedAt: string;
      ttl: number; // Time in seconds until the lock expires
    }
  | {
      lockedByUserId: null;
      lockedByUserName: null;
      reviewAssignedAt: null;
      ttl: null;
    };

export type BarcodeStatusEnum = 'unconfirmed' | 'pending' | 'confirmed';

export interface ForgeDesignNote {
  id?: string | null;
  text: string;
  createdBy?: string | null;
  createdAt?: string | null;
}

export interface ForgeDesignGroupSummary {
  designId: string;
  normalizedBarcode?: string | null;
  previewVizUrl?: string | null;
  forgeUrl?: string | null;
  barcodeStatus?: BarcodeStatusEnum | string | null;
  cropCount?: number | null;
  noteCount?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
}

export interface ForgeDesignGroupReviewSummary {
  groupId: string;
  isReviewed: boolean;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
}

export interface DesignResponse {
  _id: string;
  chainId: number;
  representativeCropId?: string;
  representativeVizUrl?: string;
  barcodeStatus: BarcodeStatusEnum;
  frontImageUrl?: string;
  backImageUrl?: string;
  barcodeImageUrl?: string;
  itemNumber?: string;
  normalizedBarcode?: string;
  notes?: ForgeDesignNote[];
  isReviewed?: boolean;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  fullGroupReview?: ForgeDesignGroupReviewSummary | null;
  fullGroupDesigns?: ForgeDesignGroupSummary[];
  partialGroupReview?: ForgeDesignGroupReviewSummary | null;
  partialGroupDesigns?: ForgeDesignGroupSummary[];
  nonFrontGroupReview?: ForgeDesignGroupReviewSummary | null;
  nonFrontGroupDesigns?: ForgeDesignGroupSummary[];
  productName?: string;
  cropCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface DesignSearchResponse {
  results: DesignResponse[];
  total: number;
  page: number;
  limit: number;
}

export interface DesignSearchParams {
  chainId: number;
  storeName?: string;
  itemNumbers?: string[];
  normalizedBarcodes?: string[];
  barcode?: string;
  page?: number;
  limit?: number;
}

export interface CreateEmptyDesignRequest {
  storeName: string;
  barcode: string;
  representativeCropId: string;
}

export interface CreateEmbeddingsCropMapping {
  product_crop_scan_id: number;
  product_embedding_crop_id: string;
}

export interface AssignDesignCropsRequest {
  cropIds: string[];
  designId: string;
}

export interface AssignDesignCropsResponse {
  designId: string;
  cropsModified: number;
  representativeCropId?: string;
}

export interface CreateDesignResponse {
  _id: string;
  cropsAssigned: number;
}
